import { skipToken, useQuery } from '@tanstack/react-query';
import { getV1CompaniesCompanyIdVetaCenterConnection } from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';

import { FeatureFlags } from '~constants/featureFlags';
import { VetaCenterConnection } from '~hooks/useVetaCenter/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import {
  SentryIgnoreRule,
  withSentryIgnoreRules,
} from '~utils/sentry/sentry-reporting-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { RARELY_CHANGED_DATA_STALE_TIME_IN_MS } from './constants';

const VETA_CENTER_CONNECTION_QUERY_KEY = 'vetaCenterConnection';
// Frequent enough that discovered contracts show up promptly.
const DISCOVERY_POLL_INTERVAL_IN_MS = 3000;

export const getVetaCenterConnectionQueryKey = (companyId: string) => [
  VETA_CENTER_CONNECTION_QUERY_KEY,
  companyId,
];

interface UseVetaCenterConnectionQueryProps {
  companyId?: string;
  discoveryBaselineInMs?: number | null;
}

async function fetchVetaCenterConnection(companyId: string, signal: AbortSignal) {
  try {
    const response = await getV1CompaniesCompanyIdVetaCenterConnection({
      path: { 'company-id': companyId },
      signal,
    });

    return VetaCenterConnection.parse(response.data.data);
  } catch (error) {
    // A company that has not connected yet has no row, so the endpoint 404s.
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }

    if (signal.aborted) {
      throw error;
    }

    try {
      connection.handleRequestError(
        error,
        t('error_messages.veta_center.failed_to_fetch_connection'),
      );
    } catch {
      // Error handling side effects already executed in connection.handleRequestError.
    }

    throw error;
  }
}

export default function useVetaCenterConnectionQuery({
  companyId: selectedCompanyId,
  discoveryBaselineInMs,
}: UseVetaCenterConnectionQueryProps = {}) {
  const { userStore } = useStores();
  const effectiveCompanyId = getEffectiveUserCompanyId(userStore);
  const companyId = selectedCompanyId ?? effectiveCompanyId;
  const isEffectiveCompany = companyId === effectiveCompanyId;
  const isFeatureFlagEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.vetaCenterIntegration,
  });
  const { canManageVetaCenterIntegration } = userStore.getPermissions();

  const canQueryVetaCenterConnection =
    isFeatureFlagEnabled && canManageVetaCenterIntegration && isEffectiveCompany;

  const { data, isError, isLoading, refetch } = useQuery({
    queryKey: getVetaCenterConnectionQueryKey(companyId),
    queryFn: canQueryVetaCenterConnection
      ? ({ signal }) =>
          withSentryIgnoreRules(SentryIgnoreRule.IGNORE_VETA_CENTER_CONNECTION_404, () =>
            fetchVetaCenterConnection(companyId, signal),
          )
      : skipToken,
    refetchInterval: ({ state }) =>
      state.data?.isDiscoveringSince(discoveryBaselineInMs)
        ? DISCOVERY_POLL_INTERVAL_IN_MS
        : false,
    retry: false,
    staleTime: RARELY_CHANGED_DATA_STALE_TIME_IN_MS,
  });

  return {
    isDiscovering: data?.isDiscoveringSince(discoveryBaselineInMs) ?? false,
    isError,
    isLoading,
    isVetaCenterConnected: Boolean(data),
    refetch,
    vetaCenterConnection: data ?? null,
  };
}
